import PointerInteraction from 'ol/interaction/Pointer';
import MapBrowserEventType from 'ol/MapBrowserEventType';
import Layer from 'ol/layer/Layer';
import Event from 'ol/events/Event';
import Overlay from 'ol/Overlay';
import { getUid } from 'ol/util';

/**
 * @enum {string}
 */
export const TooltipEventType = {
  /**
   * Triggered upon click on map
   * @event TooltipEvent#tooltipfeatures
   * @api
   */
  TOOLTIPFEATURE: 'tooltipfeature',
};

/**
 * @classdesc
 * Events emitted by {@link module:ol/interaction/Tooltip~Tooltip} instances are
 * instances of this type.
 */
export class TooltipEvent extends Event {
  /**
   * @param {TooltipEventType} type Type.
   * @param {import("../Feature").FeatureLike} feature
   * The features modified.
   * @param {import("../MapBrowserEvent.js").default} MapBrowserEvent
   * Associated {@link module:ol/MapBrowserEvent}.
   */
  constructor(type, coordinate, feature, params={}) {
    super(type);

    this.coordinate = coordinate;
    this.feature = feature;
    this.params = params;
  }
}

/**
 * @constructor
 * @extends {ol.interaction.Pointer}
 */
class Tooltip extends PointerInteraction {

  /**
   * @param {Options} [opt_options] Options.
   */
  constructor(opt_options) {
    const options = opt_options ? opt_options : {};
    super(options);

    /**
     * @type {string|undefined}
     * @private
     */
    this.cursor_ = options.cursor || 'pointer';

    /**
     * @type {string|undefined}
     * @private
     */
     this.eventType_ = options.eventType || MapBrowserEventType.POINTERMOVE;

    /**
     * @type {string|undefined}
     * @private
     */
    this.previousCursor_ = undefined;

    this.templateTooltip_ = options.templateTooltip || null;

    this.targetOverlay_ = options.targetOverlay || null;

    this.layerFilter_ = options.layers || null;
    if(this.layerFilter_) {
      if(!Array.isArray(this.layerFilter_)){
        this.layerFilter_ = [this.layerFilter_];
      }
      this.layerFilter_ = this.layerFilter_.map(layer => layer instanceof Layer ? getUid(layer) : layer);
    }

    /**
     * Tooltip overlay where tooltip are render.
     * @type {Overlay}
     * @private
     */
    this.overlay_ = undefined;

  }

  /**
   * Handles the {@link module:ol/MapBrowserEvent map browser event} (if it was a
   * doubleclick) and eventually zooms the map.
   * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
   * @return {boolean} `false` to stop event propagation.
   */
  handleEvent(mapBrowserEvent) {
    const map = mapBrowserEvent.map;
    const view = map.getView();
    const pixel = mapBrowserEvent.pixel;
    const hit = map.hasFeatureAtPixel(pixel);

    if (mapBrowserEvent.type != this.eventType_) {
      return true;
    }

    if (mapBrowserEvent.dragging) {
      return true;
    }

    if(!hit){
      this.setStatusTooltip();
      return true;
    }

    const feature = map.forEachFeatureAtPixel(pixel, (feature) => {
      return feature;
    },{
      layerFilter: (layer) => {
        let keys = [getUid(layer)];
        var originLayer = layer.get('originLayer', null);
        if(originLayer) {
          keys.push(getUid(originLayer))
        }
        return this.layerFilter_ ? keys.some(k => this.layerFilter_.includes(k)) : true;
      }
    });

    if (this.cursor_) {
      const element = map.getTargetElement();
      if(feature){
        if (element.style.cursor != this.cursor_) {
          this.previousCursor_ = element.style.cursor;
          element.style.cursor = this.cursor_;
        }
      }else if (this.previousCursor_ !== undefined) {
        element.style.cursor = this.previousCursor_;
        this.previousCursor_ = undefined;
      }
    }

    let overlay = this.getOverlay();
    if(feature){
      let coordinate = mapBrowserEvent.coordinate;
      this.dispatchEvent(
        new TooltipEvent(
          TooltipEventType.TOOLTIPFEATURE,
          coordinate,
          feature,
        )
      );
      if(overlay){
          this.setStatusTooltip(this.getContentTooltip(feature), coordinate);
      }

    }

  }

  /**
   * Remove the interaction from its current map and attach it to the new map.
   * Subclasses may set up event handlers to get notified about changes to
   * the map here.
   * @param {import("../PluggableMap.js").default} map Map.
   */
  setMap(map) {
    super.setMap(map);
    this.setOverlay();
  }

  /**
   * Get the overlay
   * @return {VectorLayer} Overlay layer.
   * @api
   */
  getOverlay() {
    return this.overlay_;
  }

  setOverlay() {
    if(this.targetOverlay_){
      if(!this.overlay_){
        let overlay = this.overlay_ = new Overlay({
            offset: [5,5],
            element: this.targetOverlay_,
            positioning: 'bottom-left'
        });
        let map = this.getMap();
        map.addOverlay(overlay);
      }
    }
  }

  setStatusTooltip(html = null, coordinate = null) {
    let overlay = this.getOverlay();
    if(overlay){
      if(html){
        overlay.setPosition(coordinate);
      }
      overlay.getElement().innerHTML = html;
      overlay.getElement().style.display = html ? 'block' : 'none';
    }
  }

  getContentTooltip(feature){
    let html = '';
    let template = this.templateTooltip_;
    if(template){
      let feat = feature;
      let props = feature.getProperties();
      if(Object.keys(props).includes("features")){
        let childs = props['features'];
        if(childs && childs.length==1){
          feat = childs[0];
        }else{
          feat = null;
        }
      }
      if(feat){
        html = template;
        props = feat.getProperties();
        Object.entries(props).forEach(([key, value]) => {
          html = html.replace(new RegExp(`:${key}`, 'g'), value);
        });
      }
    }

    return html;
  }

};

export default Tooltip;